import { motion } from 'framer-motion'
import { 
  FileText,
  Calendar,
  Mail
} from 'lucide-react'
import MarketingNav from '@/components/marketing/MarketingNav'
import MarketingFooter from '@/components/marketing/MarketingFooter'

const lastUpdated = 'January 15, 2026'

const sections = [
  {
    id: 'acceptance',
    title: 'Acceptance of Terms',
    content: `By accessing or using 3DMakerly's website, applications, and services (collectively, the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not access or use the Service.

These Terms apply to all visitors, users, and others who access or use the Service. By using the Service, you represent that you are at least 16 years of age and have the legal capacity to enter into these Terms.`
  },
  {
    id: 'service-description',
    title: 'Description of Service',
    content: `3DMakerly is an AI-native 3D manufacturing platform that provides:

- AI-powered CAD design tools and generative design capabilities
- Print prediction and real-time monitoring features
- Scan-to-CAD conversion functionality
- MakerHub marketplace for buying and selling 3D models
- Print-on-demand fulfilment services
- Cloud storage for designs and projects
- Collaboration and sharing features

The Service may be updated, expanded, or modified at any time. We reserve the right to discontinue any feature with reasonable notice.`
  },
  {
    id: 'accounts',
    title: 'User Accounts',
    content: `To access certain features of the Service, you must create an account. When creating an account, you agree to:

- Provide accurate and complete information
- Maintain the security of your account credentials
- Promptly update any changes to your information
- Accept responsibility for all activities under your account
- Notify us immediately of any unauthorised access

We reserve the right to suspend or terminate accounts that violate these Terms or for any other reason at our discretion.`
  },
  {
    id: 'subscriptions',
    title: 'Subscriptions and Payments',
    content: `Certain features of the Service require a paid subscription. By subscribing, you agree to:

**Billing:**
- Pay all applicable fees based on your selected plan
- Provide valid payment information
- Authorise us to charge your payment method

**Subscription Terms:**
- Subscriptions renew automatically unless cancelled
- You may cancel at any time; access continues until the end of the billing period
- Refunds are provided in accordance with our refund policy
- We may change pricing with 30 days' notice

**Free Trials:**
- Free trials convert to paid subscriptions unless cancelled
- One free trial per user

**Enterprise Plans:**
- Subject to separate enterprise agreements
- Custom terms may apply`
  },
  {
    id: 'user-content',
    title: 'User Content and Intellectual Property',
    content: `**Your Content:**
You retain ownership of all content you create or upload to the Service ("User Content"), including 3D models, designs, and related materials. By uploading User Content, you grant 3DMakerly a non-exclusive, worldwide, royalty-free licence to:

- Host, store, and display your content
- Process your content to provide the Service
- Create derivative works necessary to provide features (e.g., thumbnails, previews)

**Marketplace Content:**
If you publish content on MakerHub, you grant buyers the rights specified in your chosen licence. You represent that you have the right to grant such licences.

**AI Training:**
With your consent, aggregated and anonymised data may be used to improve our AI models. You can opt out of this in your settings. Raw designs and personal data are not used for training without explicit consent.

**Our Content:**
The Service, including its design, features, code, and documentation, is owned by 3DMakerly and protected by intellectual property laws. You may not copy, modify, or reverse engineer our software without permission.`
  },
  {
    id: 'acceptable-use',
    title: 'Acceptable Use',
    content: `You agree not to use the Service to:

- Violate any applicable laws or regulations
- Infringe on intellectual property rights of others
- Upload malicious software or harmful content
- Harass, abuse, or harm other users
- Attempt to gain unauthorised access to our systems
- Use the Service for competitive intelligence gathering
- Resell or redistribute the Service without authorisation
- Generate content that is illegal, harmful, or violates rights of others
- Circumvent usage limits or restrictions
- Use automated systems to access the Service without permission

We reserve the right to remove content and terminate accounts that violate these policies.`
  },
  {
    id: 'marketplace',
    title: 'Marketplace Terms',
    content: `**For Sellers:**
- You must have the right to sell the content you list
- You are responsible for accurate descriptions and pricing
- You must fulfill orders in a timely manner (for POD)
- Fees and revenue share are as specified in your seller agreement
- You must comply with all applicable laws, including tax obligations

**For Buyers:**
- You receive the rights specified in the licence chosen by the seller
- You may not redistribute purchased content except as permitted
- Refunds are subject to our marketplace refund policy
- Print-on-demand orders are subject to production and shipping times

**Commission:**
3DMakerly charges a commission on marketplace transactions as specified in the fee schedule. We may update fees with 30 days' notice.`
  },
  {
    id: 'ai-services',
    title: 'AI Services',
    content: `Our AI features, including text-to-3D generation, print prediction, and design assistance, are provided "as is" with the following terms:

**No Guarantee:**
- AI-generated content may require review and modification
- Print predictions are estimates and not guarantees
- AI suggestions should be verified by users

**Usage Limits:**
- AI generation features are subject to fair use limits
- Excessive usage may be throttled or restricted
- Enterprise plans may have custom limits

**Output Ownership:**
- You own the AI-generated content you create using our Service
- You are responsible for ensuring generated content does not infringe others' rights
- Some AI features may produce similar outputs for similar inputs`
  },
  {
    id: 'limitation',
    title: 'Limitation of Liability',
    content: `TO THE MAXIMUM EXTENT PERMITTED BY LAW:

THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED.

3DMAKERLY SHALL NOT BE LIABLE FOR:
- Indirect, incidental, special, or consequential damages
- Lost profits, data, or business opportunities
- Damages arising from reliance on AI predictions or generated content
- Failed prints or material waste
- Third-party actions or content

OUR TOTAL LIABILITY SHALL NOT EXCEED THE GREATER OF:
- The amount you paid us in the 12 months preceding the claim, or
- £100

Some jurisdictions do not allow limitation of liability, so some of these limitations may not apply to you.`
  },
  {
    id: 'indemnification',
    title: 'Indemnification',
    content: `You agree to indemnify and hold harmless 3DMakerly, its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including legal fees) arising from:

- Your use of the Service
- Your User Content
- Your violation of these Terms
- Your violation of any third-party rights`
  },
  {
    id: 'termination',
    title: 'Termination',
    content: `**By You:**
You may terminate your account at any time through your account settings or by contacting us.

**By Us:**
We may suspend or terminate your account if:
- You violate these Terms
- Your use poses a risk to us or other users
- We are required to do so by law
- We discontinue the Service

**Effect of Termination:**
- You lose access to the Service and your data
- We may retain certain data as required by law
- Provisions that should survive termination will remain in effect`
  },
  {
    id: 'governing-law',
    title: 'Governing Law and Disputes',
    content: `These Terms are governed by the laws of England and Wales.

**Dispute Resolution:**
We encourage you to contact us first to resolve any disputes informally. If a dispute cannot be resolved informally, it shall be resolved through binding arbitration in London, UK, except where prohibited by law.

**Class Action Waiver:**
You agree to resolve disputes individually and waive any right to participate in class actions.

**Exceptions:**
Either party may seek injunctive relief in court for intellectual property violations.`
  },
  {
    id: 'changes',
    title: 'Changes to Terms',
    content: `We may modify these Terms at any time. Material changes will be communicated via:
- Email notification
- In-app notification
- Notice on our website

Continued use of the Service after changes constitutes acceptance of the new Terms. If you disagree with changes, you must stop using the Service.`
  },
  {
    id: 'contact',
    title: 'Contact Information',
    content: `For questions about these Terms, contact us at:

**Email:** legal@3dmakerly.com

**Mail:**
Interlink Digital Group Limited t/a 3DMakerly
Legal Department
27 Old Gloucester Street
London, WC1N 3AX
United Kingdom

Company: 05431902 | VAT: GB132978001`
  },
]

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-surface-950">
      <MarketingNav />
      
      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 gradient-mesh opacity-20" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 mb-8"
          >
            <FileText className="w-4 h-4 text-primary-400" />
            <span className="text-sm font-medium text-primary-300">Legal</span>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            Terms of Service
          </motion.h1>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex items-center gap-4 text-surface-400"
          >
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Last updated: {lastUpdated}
            </span>
          </motion.div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-8 border-y border-surface-800 bg-surface-900/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-lg font-semibold text-white mb-4">Contents</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {sections.map((section, index) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="text-sm text-surface-400 hover:text-primary-400 transition-colors"
              >
                {index + 1}. {section.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-invert prose-surface max-w-none">
            {sections.map((section, index) => (
              <motion.div
                key={section.id}
                id={section.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="mb-12 scroll-mt-32"
              >
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="text-surface-500">{index + 1}.</span>
                  {section.title}
                </h2>
                <div className="text-surface-300 leading-relaxed whitespace-pre-line">
                  {section.content.split('\n').map((paragraph, i) => {
                    if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                      return (
                        <h3 key={i} className="text-lg font-semibold text-white mt-6 mb-2">
                          {paragraph.replace(/\*\*/g, '')}
                        </h3>
                      )
                    }
                    if (paragraph.startsWith('- ')) {
                      return (
                        <li key={i} className="ml-4 text-surface-400">
                          {paragraph.substring(2)}
                        </li>
                      )
                    }
                    if (paragraph.trim() === '') return <br key={i} />
                    return <p key={i} className="mb-4">{paragraph}</p>
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-12 bg-surface-900/30 border-t border-surface-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary-500/10 mb-4">
            <Mail className="w-6 h-6 text-primary-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Questions about our terms?</h2>
          <p className="text-surface-400 mb-4">
            Contact our legal team at{' '}
            <a href="mailto:legal@3dmakerly.com" className="text-primary-400 hover:text-primary-300 transition-colors">
              legal@3dmakerly.com
            </a>
          </p>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
